import React from "react";
import { founder, teamMembers } from "../mock";
import ContactForm from "../components/heider/ContactForm";
import Footer from "../components/heider/Footer";
import useReveal from "../hooks/useReveal";
import { Star, Instagram, ArrowRight, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const AgentCard = ({ m }) => (
  <div className="reveal-scale group text-center">
    <Link to={`/team/${m.slug}`} className="block relative mx-auto w-full aspect-square rounded-full overflow-hidden bg-neutral-200 ring-1 ring-black/[0.06] shadow-[0_18px_40px_rgba(0,0,0,0.10)]">
      <img src={m.image} alt={m.name} loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: m.imgPos || "center top", transform: `scale(${m.zoom || 1})`, transformOrigin: m.imgOrigin || "center 20%" }} />
      <div className="absolute inset-0 rounded-full bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <span className="text-white text-[10px] tracking-luxe uppercase border border-white/70 px-4 py-2">View Profile</span>
      </div>
    </Link>
    <div className="pt-5">
      <Link to={`/team/${m.slug}`}><h3 className="font-serif-display text-2xl text-[#1a1a1a] hover:text-[#b4936a] transition-colors">{m.name}</h3></Link>
      <p className="text-[10px] tracking-luxe uppercase text-neutral-500 mt-1 mb-3">{m.role}</p>
      <div className="flex items-center justify-center gap-2 mb-4">
        {m.rating ? (
          <>
            <span className="flex items-center gap-1 text-[#b4936a]"><Star size={14} fill="#b4936a" /> <span className="text-[13px] text-[#1a1a1a] font-medium">{m.rating}</span></span>
            <span className="text-[12px] text-neutral-400">({m.reviews} reviews)</span>
          </>
        ) : (
          <span className="text-[12px] text-neutral-400">No reviews yet</span>
        )}
      </div>
      {(m.phone || m.email) && (
        <div className="border-t border-neutral-200 pt-3 space-y-1.5 flex flex-col items-center">
          {m.phone && <a href={`tel:${m.phone}`} className="flex items-center gap-2 text-[12px] text-neutral-600 hover:text-[#b4936a] transition-colors"><Phone size={13} className="text-[#b4936a]" /> {m.phone}</a>}
          {m.email && <a href={`mailto:${m.email}`} className="flex items-center gap-2 text-[12px] text-neutral-600 hover:text-[#b4936a] transition-colors break-all"><Mail size={13} className="text-[#b4936a]" /> {m.email}</a>}
        </div>
      )}
    </div>
  </div>
);

const TeamPage = () => {
  useReveal();
  return (
    <div className="page-in">
      {/* Hero */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden bg-black">
        <img src="https://images.unsplash.com/photo-1638284457192-27d3d0ec51aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200" alt="Team" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center text-white px-6">
          <p className="text-[11px] tracking-luxe uppercase text-white/80 mb-6 fade-in" style={{ opacity: 0, animationDelay: "0.4s" }}>The DMV's #1 Real Estate Team</p>
          <h1 className="font-serif-display font-light text-5xl md:text-7xl lg:text-8xl leading-tight clip-line">
            <span style={{ opacity: 0 }}>Meet Capital Corner Group</span>
          </h1>
        </div>
      </section>

      {/* Lead - Omer */}
      <section className="bg-[#f3f1ec] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="reveal-left relative">
            <div className="relative overflow-hidden aspect-[4/5] max-w-[520px] mx-auto lg:mx-0">
              <img src={founder.image} alt={founder.name} className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 right-4 lg:right-8 bg-[#0d0d0d] text-white px-6 py-4 flex items-center gap-3">
              <Star size={16} fill="#b4936a" className="text-[#b4936a]" />
              <div>
                <p className="font-serif-display text-xl leading-none">5.0</p>
                <p className="text-[9px] tracking-luxe uppercase text-white/60 mt-1">23 Team Reviews</p>
              </div>
            </div>
          </div>
          <div className="reveal">
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-4">Real Broker Lead &middot; (He/Him/His)</p>
            <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl font-light text-[#1a1a1a] mb-6">Omer <span className="italic">Reshid</span></h2>
            <div className="space-y-5 text-neutral-600 text-[15px] leading-relaxed max-w-xl">
              {founder.bio.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <Link to="/team/omer-reshid" className="inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase text-white bg-[#0d0d0d] px-6 py-3 hover:bg-[#b4936a] transition-colors">
                View Full Profile <ArrowRight size={14} />
              </Link>
              <a href={founder.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase text-[#1a1a1a] border border-[#1a1a1a] px-6 py-3 hover:bg-[#1a1a1a] hover:text-white transition-colors">
                <Instagram size={15} /> {founder.handle}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Agents grid */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <div className="text-center mb-16 reveal">
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-4">The Advisors</p>
            <h2 className="font-serif-display text-4xl md:text-6xl font-light text-[#1a1a1a]">A Team Built to Win</h2>
            <p className="text-neutral-500 text-[15px] leading-relaxed max-w-2xl mx-auto mt-6">When you hire one of us, you get all of us &mdash; a full bench of specialists covering every corner of the DMV.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-16 reveal">
            <div className="overflow-hidden group">
              <img src="/team/group-2.webp" alt="The Capital Corner Group team" loading="lazy" className="w-full h-[280px] md:h-[440px] object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
            </div>
            <div className="overflow-hidden group">
              <img src="/team/group-1.webp" alt="The Capital Corner Group team" loading="lazy" className="w-full h-[280px] md:h-[440px] object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
            </div>
          </div>
          <div className="flex gap-5 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-6 pr-6 cc-scroll">
            {teamMembers
              .filter((m) => m.slug !== "omer-reshid")
              .slice()
              .sort((a, b) => (a.image.includes("logo-avatar") ? 1 : 0) - (b.image.includes("logo-avatar") ? 1 : 0))
              .map((m) => (
              <div key={m.name} className="snap-start shrink-0 w-[220px] md:w-[240px]"><AgentCard m={m} /></div>
            ))}
          </div>
          <p className="text-center text-neutral-400 text-[11px] tracking-luxe uppercase mt-2">Swipe to meet the team &rarr;</p>
          <div className="text-center mt-16 reveal">
            <a href="#contact" className="inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase text-[#1a1a1a] border border-[#1a1a1a] px-8 py-4 hover:bg-[#1a1a1a] hover:text-white transition-colors">
              Join Our Team <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
};

export default TeamPage;
